import { create } from 'zustand';

/**
 * PTV2-1157 — a transient "flash these required fields" signal for the pedigree node editor.
 *
 * When the user tries to match a pedigree individual to an existing patient but the node is missing
 * one of the four required demographics (first/last name, DOB, sex), the match control raises this
 * signal; the reused `PersonalInfoCard` (which owns those field rows) reads it, auto-expands, and
 * pulses a red ring on the offending rows. Keyed by person guid so only the open node's card reacts;
 * a bumped `nonce` lets the same field set flash again on a repeat click.
 *
 * Lives in patient-record `_shared` (not the pedigree feature) so `PersonalInfoCard` can consume it
 * without a patient-record → pedigree dependency. On the plain patient record it is never raised, so
 * the card behaves exactly as before (zero impact).
 */
export type FlashFieldKey = 'firstName' | 'lastName' | 'dateOfBirth' | 'sex';

interface FieldFlashState {
  /** Person guid whose card should flash; '' when idle. */
  readonly guid: string;
  /** Which required rows to ring. */
  readonly fields: ReadonlySet<FlashFieldKey>;
  /** Bumped on each raise so a repeat with the same fields still triggers. */
  readonly nonce: number;
  readonly flash: (guid: string, fields: readonly FlashFieldKey[]) => void;
}

export const useFieldFlashStore = create<FieldFlashState>((set) => ({
  guid: '',
  fields: new Set<FlashFieldKey>(),
  nonce: 0,
  flash: (guid, fields) => {
    set((state) => ({ guid, fields: new Set(fields), nonce: state.nonce + 1 }));
  },
}));
